/* "Add a Rakhi" popup: opened by any [data-rakhi-open] trigger (the cart drawer
   header button, which is re-rendered on every cart AJAX call — hence the
   delegated listener on document), adds a rakhi straight to the cart. */
if (!customElements.get('rakhi-popup')) {
  class RakhiPopup extends HTMLElement {
    connectedCallback() {
      document.addEventListener('click', this.handleDocumentClick.bind(this));
      document.addEventListener('keydown', (evt) => {
        if (evt.key === 'Escape' && !this.hidden) this.toggle(false);
      });
      this.addEventListener('click', this.handleClick.bind(this));
      // Cart can change from anywhere (drawer remove, quantity selectors) — re-read it.
      const sync = (evt) => this.syncFromCart(evt.detail?.cart);
      document.addEventListener('on:cart:add', sync);
      document.addEventListener('on:line-item:change', sync);
    }

    handleDocumentClick(evt) {
      if (evt.target.closest('[data-rakhi-open]')) {
        evt.preventDefault();
        this.toggle(true);
      }
    }

    handleClick(evt) {
      if (evt.target.closest('[data-rakhi-close]')) {
        this.toggle(false);
        return;
      }

      const stepBtn = evt.target.closest('[data-rakhi-step]');
      if (stepBtn) {
        this.setQty(stepBtn.closest('.rakhi-popup__qty'), parseInt(stepBtn.dataset.rakhiStep, 10));
        return;
      }

      const addBtn = evt.target.closest('[data-rakhi-add]');
      if (addBtn) this.addToCart(addBtn);
    }

    toggle(open) {
      // No body scroll lock — the cart drawer already owns that while it's open.
      this.hidden = !open;
      if (open) {
        this.querySelector('[data-rakhi-close]')?.focus();
        this.syncFromCart();
      }
    }

    async addToCart(btn) {
      const routes = window.theme?.routes || {};
      btn.classList.add('is-loading');

      try {
        const response = await fetch(routes.cartAdd || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: parseInt(btn.dataset.rakhiAdd, 10), quantity: 1 })
        });
        if (!response.ok) throw new Error(response.status);
        const data = await response.json();

        // Cart count bubble.
        const bubbleResponse = await fetch(`${routes.cart || '/cart'}?section_id=cart-icon-bubble`);
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubbleResponse.ok && bubble) bubble.innerHTML = await bubbleResponse.text();

        // Tell the rest of the theme (quantity selectors, reward ladder, drawer).
        const cartResponse = await fetch(`${routes.cart || '/cart'}.js`);
        if (cartResponse.ok) {
          document.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: { cart: await cartResponse.json(), variantId: data.variant_id }
          }));
        }
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));

        this.showQty(btn, data.quantity || 1);
      } catch (error) {
        document.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: { error: error?.message }
        }));
      } finally {
        btn.classList.remove('is-loading');
      }
    }

    // Show each row's pill (or the Add button) to match what's actually in the cart.
    async syncFromCart(cart) {
      if (!cart) {
        const response = await fetch(`${window.theme?.routes?.cart || '/cart'}.js`);
        if (!response.ok) return;
        cart = await response.json();
      }

      this.querySelectorAll('[data-rakhi-add]').forEach((btn) => {
        const variantId = parseInt(btn.dataset.rakhiAdd, 10);
        const item = cart.items?.find((line) => line.variant_id === variantId);
        if (item?.quantity > 0) {
          this.showQty(btn, item.quantity);
        } else {
          if (btn.nextElementSibling?.classList.contains('rakhi-popup__qty')) {
            btn.nextElementSibling.hidden = true;
          }
          btn.hidden = false;
        }
      });
    }

    // Replace the Add button with a -/n/+ pill (built once, reused after).
    showQty(btn, quantity) {
      let qty = btn.nextElementSibling;
      if (!qty?.classList.contains('rakhi-popup__qty')) {
        qty = document.createElement('div');
        qty.className = 'rakhi-popup__qty';
        qty.innerHTML =
          '<button type="button" data-rakhi-step="-1" aria-label="Decrease quantity">\u2212</button>' +
          '<span>1</span>' +
          '<button type="button" data-rakhi-step="1" aria-label="Increase quantity">+</button>';
        btn.after(qty);
      }
      qty.querySelector('span').textContent = quantity;
      qty.hidden = false;
      btn.hidden = true;
    }

    async setQty(qty, step) {
      const valueEl = qty.querySelector('span');
      const btn = qty.previousElementSibling;
      const variantId = parseInt(btn.dataset.rakhiAdd, 10);
      const next = Math.max(0, parseInt(valueEl.textContent, 10) + step);

      qty.classList.add('is-loading');
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: String(variantId), quantity: next })
        });
        if (!response.ok) throw new Error(response.status);

        valueEl.textContent = next;
        document.dispatchEvent(new CustomEvent('on:cart:add', {
          bubbles: true,
          detail: { cart: await response.json(), variantId }
        }));
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
        if (next === 0) {
          qty.hidden = true;
          btn.hidden = false;
        }
      } catch (error) {
        document.dispatchEvent(new CustomEvent('on:cart:error', { bubbles: true, detail: { error: error?.message } }));
      } finally {
        qty.classList.remove('is-loading');
      }
    }
  }

  customElements.define('rakhi-popup', RakhiPopup);
}
